import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  AiNotebooksUrl,
  AiNotebooksGeneralInformationUrl,
  AiNotebooksAttachedDataUrl,
  AiNotebooksCodeEditorUrl,
} from "../../utils/urls";
import RightArrow from "./../../assets/icons/arrow-right.svg?react";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Helmet } from "react-helmet";
import { Skeleton, TabOptions, Tabs, Typography } from "djuno-design";
import NoticeIcon from "./../../assets/icons/logs/notice.svg?react";
import ChainIcon from "./../../assets/icons/chain.svg?react";
import CommandPaletteIcon from "./../../assets/icons/command-line.svg?react";
import {
  getAiNotebookAsync,
  handleClearAiNotebookSlice,
  selectAiNotebook,
  selectAiNotebookLoading,
} from "../../store/ai/notebooks/notebookSlice";
import { AiNotebooksRefreshStatus } from "../../store/ai/notebooks/notebooksSlice";

export const generateServiceRoutes = (notebookId: string) => {
  const options: TabOptions = [];

  const generalInformationOption = {
    label: (
      <div className="flex items-center gap-1">
        <NoticeIcon className="w-3" />
        General information
      </div>
    ),
    url: AiNotebooksGeneralInformationUrl(notebookId),
    testId: "1th-tab",
  };

  const attachedDataOption = {
    label: (
      <div className="flex items-center gap-1">
        <ChainIcon className="w-3" />
        Attached data
      </div>
    ),
    url: AiNotebooksAttachedDataUrl(notebookId),
    testId: "2th-tab",
  };

  const codeEditorOption = {
    label: (
      <div className="flex items-center gap-1">
        <CommandPaletteIcon className="w-3" />
        Code editor
      </div>
    ),
    url: AiNotebooksCodeEditorUrl(notebookId),
    testId: "code-editor",
  };

  options.push(generalInformationOption);
  options.push(attachedDataOption);
  options.push(codeEditorOption);

  return options;
};

const AiNotebookPage = () => {
  const { notebookId, engine } = useParams();

  const notebook = useAppSelector(selectAiNotebook);
  const notebookLoading = useAppSelector(selectAiNotebookLoading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const AiNotebookPageMenus = useMemo(() => {
    if (notebookId) {
      return generateServiceRoutes(notebookId);
    }
    return [];
  }, [notebookId]);

  useEffect(() => {
    if (notebookId) {
      dispatch(getAiNotebookAsync({ id: notebookId }));
    }

    return () => {
      dispatch(handleClearAiNotebookSlice());
    };
  }, [dispatch, notebookId, engine]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (
      notebook &&
      AiNotebooksRefreshStatus.includes(
        notebook.status.state as unknown as string
      )
    ) {
      interval = setInterval(() => {
        dispatch(
          getAiNotebookAsync({
            id: notebook.id,
            withoutLoading: true,
          })
        );
      }, 20000);
    } else {
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, notebook]);

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | AI Notebook (
          {notebook ? notebook.spec.name : ""})
        </title>
        <meta name="description" content="" />
      </Helmet>
      <div className="flex items-center justify-between h-16">
        <div className="px-6 items-center justify-between flex flex-1 transition duration-150">
          <div className="flex items-center gap-2">
            <RightArrow
              onClick={() => {
                navigate(AiNotebooksUrl);
              }}
              className="rotate-180 w-5 h-5 hover:scale-110 transition-all duration-500 text-slate-800 dark:text-slate-100 cursor-pointer"
            />
            {notebook && !notebookLoading && (
              <>
                <div className="flex flex-col">
                  <Typography.Title level={5} className="!mb-0">
                    {notebook.spec.name}
                  </Typography.Title>
                </div>
              </>
            )}
            {notebookLoading && (
              <>
                <Skeleton
                  shape="rectangle"
                  style={{ width: 200, height: 30 }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 mt-5">
        <div className="h-11">
          <Tabs options={AiNotebookPageMenus} />
        </div>
        <div className="py-6">
          <Outlet />
        </div>
      </div>
    </>
  );
};
export default AiNotebookPage;
